import { action, autorun, makeAutoObservable, runInAction, toJS } from 'mobx';
import { dateFormatters, getEffectiveDates } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { RootStore } from 'store/rootStore';
import { Cart } from 'types/cartService';
import { GroupSource } from 'types/groupService';
import { CartBrowsingCriteria } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';

interface FormFields extends DefaultFormFields {
  effectiveDate: FormControl<string>;
  zipCode: FormControl<string>;
  county: FormControl<string>;
  state: FormControl<string>;
  naics: FormControl<string>;
  sic4: FormControl<string>;
  groupSize: FormControl<string>;
}

export class BrowseInfoStore {
  rootStore: RootStore;

  form: FormController<FormFields>;
  effectiveDates: OptionType[] = [];
  ineligibleState = false;
  loading = false;
  additionalComponentTypes: string[] = [];

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  async requestZipCodeInfo(): Promise<void> {
    this.setLoading(true);
    await this.rootStore.geoStore.requestZipCodeInfo(this.form.fields.zipCode.value);
    runInAction(() => {
      this.setLoading(false);
      const { wrongZipCodeKeyedIn, zipCodeAffiliatedData } = this.rootStore.geoStore;
      if (!wrongZipCodeKeyedIn) {
        this.form.fields.state.value = zipCodeAffiliatedData.state.id;
        this.form.fields.county.value = zipCodeAffiliatedData.counties[0].name;
        this.ineligibleState = !this.rootStore.domainStore.isEligibleState(zipCodeAffiliatedData.state.id);
      }
    });
  }

  async submitBrowseInfoForm(): Promise<Cart | undefined> {
    this.setLoading(true);
    const now = new Date();
    const currentDate = now.toISOString().slice(0, 10);
    const inputGroup = {
      id: '',
      name: `${currentDate}`,
      source: GroupSource.ESTORE,
      effectiveDate: this.form.fields.effectiveDate.value,
      contactInfo: { primaryAddress: { city: '', street: '', street2: '', zip: '', state: '', county: '' } },
      agentOfRecord: null,
    };

    await this.rootStore.groupStore.createGroup(inputGroup);
    return runInAction(async (): Promise<Cart | undefined> => {
      const id = this.rootStore.groupStore.group.id;
      if (id) {
        const createdCart = (
          await this.rootStore.cartStore.createCart(
            id,
            'ESTORE',
            // Set the CartBrowsingCriteria type to use the default browsing criteria that will be set in BE.
            {
              effectiveDate: this.form.fields.effectiveDate.value,
              zipCode: this.form.fields.zipCode.value,
              state: this.form.fields.state.value,
              county: this.form.fields.county.value,
              naics: this.form.fields.naics.value,
              sic4: this.form.fields.sic4.value,
              groupSize: this.getGroupSizeBrowsingCriteria(this.form.fields.groupSize.value),
              numberOfEmployees: this.form.fields.groupSize.value.trim(),
            } as CartBrowsingCriteria,
            this.rootStore.domainStore.domainHeader.id,
          )
        ).data?.createCart;

        this.setLoading(false);
        return createdCart;
      }
      return undefined;
    });
  }

  overrideNAICSValue(value: string): void {
    this.form.populateFields({ naics: value });
  }

  overrideSICSearchValue(sic4: string): void {
    this.form.populateFields({ sic4 });
  }

  isAdditionalInputsValid(searchElement: string, length: number, formField: string): boolean {
    const newProposalAdditionalInputs = toJS(this.rootStore.domainStore.domainHeader).properties
      ?.newProposalAdditionalInputs;

    const isRequired = !!newProposalAdditionalInputs?.required.includes(searchElement);

    return isRequired ? formField.length === length : formField.length === length || formField.length === 0;
  }

  // !Important! - GROUP_SIZE component is ESTORE specific component
  // Unlike NAICS, or SIC component, GROUP_SIZE component has additional metadata:
  // Examples: GROUP_SIZE_2-9_10-50, GROUP_SIZE_2-4_5-9_10-49
  // So the first part of the Group Size component is a component type - "GROUP_SIZE" that we have to cut from the second part
  // The second part consist of the ranges (number dash number) combined with underscores ('_')
  // To check that the input value is in the range, we have to split
  get isGroupSizeValid(): boolean {
    const newProposalAdditionalInputs = toJS(this.rootStore.domainStore.domainHeader).properties
      ?.newProposalAdditionalInputs;

    const isRequired =
      (
        newProposalAdditionalInputs?.required
          .map((require) => this.parseGroupSize(require))
          .find((requiredList) => !!requiredList && requiredList.length > 0) || []
      ).length > 0 || false;

    return (
      (!isRequired && this.form.fields.groupSize.value.length === 0) || // valid in case if the field is optional and empty
      this.getGroupSizeBrowsingCriteria(this.form.fields.groupSize.value).length > 0 // valid only we can convert
    );
  }

  getGroupSizeRanges(): string[] {
    const newProposalAdditionalInputs = toJS(this.rootStore.domainStore.domainHeader).properties
      ?.newProposalAdditionalInputs;

    const requiredGroupSize =
      newProposalAdditionalInputs?.required
        .map((require) => this.parseGroupSize(require))
        .find((requiredList) => !!requiredList && requiredList.length > 0) || [];
    const optionalGroupSize =
      newProposalAdditionalInputs?.optional
        .map((optional) => this.parseGroupSize(optional))
        .find((optionalList) => !!optionalList && optionalList.length > 0) || [];

    return [...(requiredGroupSize || []), ...(optionalGroupSize || [])];
  }

  // eslint-disable-next-line class-methods-use-this
  getGroupSizeBrowsingCriteria(value: string): string {
    const ranges = this.getGroupSizeRanges();
    if (ranges.length === 0) return '';

    let foundRange = '';
    const num = parseInt(value, 10);

    if (!Number.isNaN(num)) {
      ranges.forEach((range) => {
        const rangeLimits = range.split('-');
        if (rangeLimits.length !== 2) return;
        if (num >= parseInt(rangeLimits[0], 10) && num <= parseInt(rangeLimits[1], 10)) {
          foundRange = range;
        }
      });
    }
    return foundRange;
  }

  // eslint-disable-next-line class-methods-use-this
  parseGroupSize(input: string): string[] | null {
    const regex = /^GROUP_SIZE_((?:\d+-\d+_?)+)$/;

    const matches = input.match(regex);

    if (!matches || matches.length !== 2) {
      return null;
    }
    return matches[1].split('_');
  }

  get canBrowseNext(): boolean {
    return (
      this.form.fields.zipCode.value.length === 5 &&
      this.form.fields.effectiveDate.value.length > 0 &&
      this.form.fields.state.value.length > 0 &&
      !this.ineligibleState &&
      this.isAdditionalInputsValid('NAICS', 6, this.form.fields.naics.value) &&
      this.isAdditionalInputsValid('SIC', 4, this.form.fields.sic4.value) &&
      this.isGroupSizeValid
    );
  }

  resetBrowseInfo(): void {
    this.form.resetForm();
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.form = new FormController({
      effectiveDate: new FormControl<string>(''),
      zipCode: new FormControl<string>(''),
      county: new FormControl<string>(''),
      state: new FormControl<string>(''),
      naics: new FormControl<string>(''),
      sic4: new FormControl<string>(''),
      groupSize: new FormControl<string>(''),
    });

    autorun(() => {
      if (this.form.fields.zipCode.value.length === 5) {
        void this.requestZipCodeInfo();
      } else {
        this.form.fields.state.overrideValue('');
        this.form.fields.county.overrideValue('');
        this.ineligibleState = false;
      }
    });

    autorun(() => {
      this.effectiveDates = getEffectiveDates(
        this.rootStore.domainStore.domainHeader.properties.effectiveDatesAmount || 4,
        this.rootStore.domainStore.domainHeader.properties.productsAvailableYear,
        this.rootStore.domainStore.domainHeader.properties.retroEstoreQuotingDaysAmount,
        this.rootStore.domainStore.domainHeader.properties.productsAvailableMonth,
      ).map((date) => ({
        value: date,
        label: dateFormatters.normDateToLongAmericanDate(date),
      }));
    });

    autorun(() => {
      const newProposalAdditionalInputs = toJS(this.rootStore.domainStore.domainHeader).properties
        ?.newProposalAdditionalInputs;
      this.additionalComponentTypes = [
        ...(newProposalAdditionalInputs?.required.map((require) => {
          if (this.parseGroupSize(require)) return 'GROUP_SIZE'; // if it's a GROUP_SIZE component cut all the "metadata"
          return require; // else leave it as is
        }) || []),
        ...(newProposalAdditionalInputs?.optional.map((optional) => {
          if (this.parseGroupSize(optional)) return 'GROUP_SIZE';
          return optional;
        }) || []),
      ];
    });

    makeAutoObservable(
      this,
      {
        setLoading: action,
      },
      { autoBind: true },
    );
  }
}
